import { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMe } from "@/hooks";
import { http } from "@/api";
import { qk } from "@/hooks/queryKeys";

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const me = useMe();
  const location = useLocation();
  useTimezoneAutoSync(me.data?.timezone ?? null, !!me.data);

  if (me.isLoading) return <div className="h-screen grid place-items-center text-fg-3 text-body-s">Loading…</div>;
  if (!me.data) return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  return <>{children}</>;
}

// One-time auto-detect: if the signed-in user has no saved timezone, read it
// from the browser and PUT to /api/me/timezone. Subsequent loads will see the
// value populated and skip. The ref guard makes sure we don't fire twice in
// the same React Strict-mode double-mount.
function useTimezoneAutoSync(currentTz: string | null, isAuthed: boolean): void {
  const qc = useQueryClient();
  const firedRef = useRef(false);
  const mut = useMutation({
    mutationFn: (timezone: string) =>
      http("/api/me/timezone", { method: "PUT", body: JSON.stringify({ timezone }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me() }),
  });

  useEffect(() => {
    if (!isAuthed) return;
    if (currentTz) return;             // already set
    if (firedRef.current) return;
    firedRef.current = true;
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz && /^[A-Za-z][A-Za-z0-9_+\-/]*$/.test(tz)) mut.mutate(tz);
    } catch {
      // Browser doesn't support Intl tz — leave null, user sets manually in settings.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, currentTz]);
}
